import React, { useEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    ArrowLeft,
    CheckCircle,
    Clock,
    Eye,
    EyeOff,
    Lock,
    LogIn,
    Mail,
    Shield,
    Building2
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import heLogo from '../Assets/HE.AI-Logo.png';


const API = API_BASE_URL;

const Login = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const prefilledEmail = searchParams.get('email');
    const prefilledSchoolId = searchParams.get('school_id');
    const isActivation = searchParams.get('activate') === 'true';

    const [stage, setStage] = useState(isActivation ? 'activationSetup' : 'login');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [email, setEmail] = useState(prefilledEmail || '');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [schoolId, setSchoolId] = useState(prefilledSchoolId || '');
    const [availableSchools, setAvailableSchools] = useState([]);
    const [forgotPasswordSchools, setForgotPasswordSchools] = useState([]);

    const [otp, setOtp] = useState(Array(6).fill(''));
    const otpRefs = useRef([]);

    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [otpTimeLeft, setOtpTimeLeft] = useState(0);
    const [rememberMe, setRememberMe] = useState(false);

    const otpTimerRef = useRef(null);

    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        match: false
    });

    useEffect(() => {
        if (localStorage.getItem('accessToken')) {
            navigate('/HomePage');
        }
        return () => {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        };
    }, [navigate]);

    useEffect(() => {
        const newStrength = {
            length: newPassword.length >= 8,
            uppercase: /[A-Z]/.test(newPassword),
            lowercase: /[a-z]/.test(newPassword),
            number: /[0-9]/.test(newPassword),
            special: /[^A-Za-z0-9]/.test(newPassword),
            match: newPassword === confirmPassword && newPassword !== ''
        };
        setPasswordStrength(newStrength);
    }, [newPassword, confirmPassword]);

    const startOtpTimer = () => {
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        setOtpTimeLeft(10 * 60);
        otpTimerRef.current = setInterval(() => {
            setOtpTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const handleLogin = async () => {
        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${API}/login/parent`,
                new URLSearchParams({ username: email.toLowerCase(), password }),
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );
            const data = response.data;
            if (data.status === 'school_selection_required') {
                setAvailableSchools(data.schools);
                setStage('schoolSelection');
            } else if (data.status === 'setup_required') {
                setSchoolId(data.school_id);
                setStage('activationSetup');
                setSuccessMessage('Your account needs to be activated. Please enter your temporary password.');
            } else if (data.access_token) {
                handleLoginSuccess(data);
            }
        } catch (error) {
            console.error('Login error:', error);
            if (error.response?.status === 401) {
                setError('Email or password is incorrect.');
            } else if (error.response?.status === 403) {
                setError('Your account is not active. Please contact the school admin.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Login failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const completeLogin = async (selectedSchoolId) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${API}/login/complete`, {
                email: email.toLowerCase(),
                password,
                role: 'parent',
                school_id: selectedSchoolId
            }, {
                headers: { 'Content-Type': 'application/json' }
            });
            const data = response.data;
            if (data.status === 'setup_required') {
                setSchoolId(selectedSchoolId);
                setStage('activationSetup');
            } else if (data.access_token) {
                handleLoginSuccess(data);
            }
        } catch (error) {
            console.error('Login completion error:', error);
            if (error.response?.status === 403) {
                setError('Account setup required. Please activate your account first.');
                setStage('activationSetup');
            } else {
                setError(error.response?.data?.detail || error.message || 'Login failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Single-step activation: verify temp password and set new password, no OTP
    // (matches the simplified Student activation flow — parents are created in bulk).
    const handleActivation = async () => {
        if (!email || !password) {
            setError('Please enter your email and temporary password');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        const isStrong = passwordStrength.length && passwordStrength.uppercase && passwordStrength.lowercase && passwordStrength.number && passwordStrength.special;
        if (!isStrong) {
            setError('Please choose a stronger password that meets all requirements');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${API}/parent/activate`, {
                email: email.toLowerCase(),
                temp_password: password,
                new_password: newPassword,
                school_id: schoolId
            });
            handleLoginSuccess(response.data);
        } catch (error) {
            console.error('Parent activation error:', error);
            const status = error.response?.status;
            if (status === 401) {
                setError('That temporary password doesn\'t match. Try again or contact support.');
            } else if (status === 404) {
                setError('We couldn\'t find that account. Check your email and school.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Activation failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPasswordRequest = async () => {
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            // First, find schools for this email
            const response = await axios.post(`${API}/password/forgot/find-schools`, {
                email: email.toLowerCase()
            });

            const schools = response.data.schools;

            if (schools.length === 1) {
                // If only one school, proceed directly
                await sendPasswordResetOTP(schools[0].id);
            } else {
                // Multiple schools, show selection
                setForgotPasswordSchools(schools);
                setStage('forgotPasswordSchoolSelection');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const sendPasswordResetOTP = async (selectedSchoolId) => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${API}/password/forgot`, {
                email: email.toLowerCase(),
                school_id: selectedSchoolId
            });

            // Store the school ID for later use in password reset
            setSchoolId(selectedSchoolId);
            setSuccessMessage('Password reset code sent to your email');
            setStage('resetPasswordOtp');
            startOtpTimer();
        } catch (error) {
            console.error('Send OTP error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter the complete 6-digit verification code');
            return;
        }
        if (otpTimeLeft <= 0) {
            setError('Reset code has expired. Please request a new one.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            if (!schoolId) {
                setError('School information is missing. Please try the forgot password process again.');
                return;
            }

            await axios.post(`${API}/password/reset`, {
                email: email.toLowerCase(),
                otp: parseInt(otpString, 10),
                new_password: newPassword,
                school_id: schoolId
            });
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
            setSuccessMessage('Password reset successful! You can now log in with your new password.');
            setStage('login');
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setOtp(Array(6).fill(''));
        } catch (error) {
            console.error('Password reset error:', error);
            if (error.response?.status === 400) {
                setError('That code is invalid or expired. Request a new one.');
            } else if (error.response?.status === 404) {
                setError('Account not found. Please check your email and school.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Reset failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginSuccess = (data) => {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token || '');
        localStorage.setItem('school_id', data.school_id || '');
        localStorage.setItem('school_name', data.school_name || '');
        localStorage.setItem('user_role', 'parent');
        localStorage.setItem('user_email', email.toLowerCase());
        navigate('/HomePage');
    };

    const handleOtpChange = (value, idx) => {
        if (!/^[0-9]?$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[idx] = value;
        setOtp(newOtp);
        if (value && idx < otp.length - 1) {
            otpRefs.current[idx + 1].focus();
        }
    };

    const handleOtpKeyDown = (e, idx) => {
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1].focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').trim();
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);
            otpRefs.current[5].focus();
        }
    };

    const goToForgotPassword = () => {
        setStage('resetPassword');
        setError('');
        setSuccessMessage('');
    };

    const goToLogin = () => {
        setStage('login');
        setError('');
        setSuccessMessage('');
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    };

    const handleBackToCredentials = () => {
        setStage('login');
        setAvailableSchools([]);
        setForgotPasswordSchools([]);
        setError('');
    };

    const handleKeyPress = (e, callback) => {
        if (e.key === 'Enter') {
            callback();
        }
    };

    return (
        <div className="min-h-screen text-gray-800 font-['Poppins',sans-serif]">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
                
                .bg-slideshow { position: fixed; inset: 0; z-index: 0; overflow: hidden; }
                .bg-slide {
                    position: absolute; inset: 0;
                    background-size: cover; background-position: center;
                    opacity: 0; animation: bgfade 15s infinite ease-in-out;
                    will-change: opacity;
                }
                .bg-slide:nth-child(1) { animation-delay: 0s; }
                .bg-slide:nth-child(2) { animation-delay: 2.5s; }
                .bg-slide:nth-child(3) { animation-delay: 5s; }
                .bg-slide:nth-child(4) { animation-delay: 7.5s; }
                .bg-slide:nth-child(5) { animation-delay: 10s; }
                .bg-slide:nth-child(6) { animation-delay: 12.5s; }
                
                @keyframes bgfade {
                    0% { opacity: 0; }
                    8% { opacity: 1; }
                    28% { opacity: 1; }
                    40% { opacity: 0; }
                    100% { opacity: 0; }
                }
                
                .gradient-overlay {
                    position: fixed; inset: 0; z-index: 1; pointer-events: none;
                    background: radial-gradient(1200px 800px at 15% 20%, rgba(249,115,22,0.20), transparent 60%),
                                radial-gradient(900px 700px at 85% 30%, rgba(251,146,60,0.16), transparent 60%),
                                linear-gradient(120deg, rgba(234,88,12,0.08), rgba(249,115,22,0.06), rgba(234,88,12,0.08));
                }
                
                .top-white-fade {
                    position: fixed; inset: 0; z-index: 2; pointer-events: none;
                    background: linear-gradient(to bottom,
                                rgba(255,255,255,0.98) 0%,
                                rgba(255,255,255,0.90) 12%,
                                rgba(255,255,255,0.72) 24%,
                                rgba(255,255,255,0.38) 42%,
                                rgba(255,255,255,0.10) 60%,
                                rgba(255,255,255,0.00) 75%);
                }
                
                .panel-glass {
                    background: rgba(255,255,255,0.97);
                    border: 1px solid rgba(255,255,255,0.35);
                }
                
                .soft-shadow {
                    box-shadow: 0 34px 68px -24px rgba(0,0,0,0.40),
                                0 22px 44px -26px rgba(249,115,22,0.22);
                }
                
                .focus-lift:focus-within {
                    transform: translateY(-1px);
                    box-shadow: 0 10px 30px rgba(249, 115, 22, 0.18);
                }
                
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(22px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-28px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                
                .animate-fadeInUp { animation: fadeInUp 0.6s ease-out both; }
                .animate-slideInLeft { animation: slideInLeft 0.6s ease-out both; }
                
                .marquee-outer { position: relative; overflow: hidden; width: 100%; }
                .marquee-track {
                    display: flex; width: max-content;
                    animation: scroll-left 23.4s linear infinite;
                    color: #ea580c;
                }
                .marquee-chunk { white-space: nowrap; }
                
                @keyframes scroll-left {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
            `}</style>

            {/* Background layers */}
            <div className="bg-slideshow" aria-hidden="true">
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/13794/SM496503.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/12135/SM377070.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/19549/SM940390.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14229/SM523032.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/17072/SM743851.jpg')" }}></div>
                <div className="bg-slide"
                    style={{ backgroundImage: "url('https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14550/SM543105.jpg')" }}></div>
            </div>
            <div className="gradient-overlay" aria-hidden="true"></div>
            <div className="top-white-fade" aria-hidden="true"></div>

            {/* Header */}
            <header className="relative z-10">
                <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4 animate-slideInLeft">
                        <img
                            src={heLogo}
                            alt="Holistic Evaluator Logo"
                            className="h-20 md:h-24 w-auto object-contain drop-shadow"
                        />
                    </div>
                    <div className="hidden md:block text-right">
                        <p className="text-gray-900 text-base md:text-lg font-medium">Let's build National Assets
                            together</p>
                        <p className="text-gray-700 text-sm">Empowering parents, inspiring futures</p>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="relative z-10">
                <section className="min-h-[calc(100vh-88px)] flex items-center justify-center px-4 pb-10">
                    <div className="w-full max-w-md">
                        <div className="panel-glass soft-shadow rounded-2xl p-8 animate-fadeInUp">
                            {/* Title based on stage */}
                            <div className="text-center mb-4">
                                <h3 className="text-base font-semibold text-orange-600">
                                    {stage === 'login' && 'Empowering Parents for #ViksitBharat2047'}
                                    {stage === 'schoolSelection' && 'Your Child\'s Education Journey'}
                                    {stage === 'activationSetup' && 'Secure Your Account'}
                                    {stage === 'resetPassword' && 'Reset Your Password'}
                                    {stage === 'forgotPasswordSchoolSelection' && 'Select Your School'}
                                    {stage === 'resetPasswordOtp' && 'Create New Password'}
                                </h3>
                            </div>

                            {/* Icon + Badge */}
                            <div className="text-center mb-6">
                                <img
                                    src="https://www.svgrepo.com/show/483726/parent-and-child.svg"
                                    alt="Parent Icon"
                                    className="mx-auto w-[108px] h-[108px] mb-0"
                                />
                                <div
                                    className="inline-block bg-orange-600 text-white px-5 py-1.5 rounded-full shadow -mt-1">
                                    <span className="text-[15px] font-semibold">
                                        {stage === 'login' && 'Parent Login'}
                                        {stage === 'schoolSelection' && 'Select School'}
                                        {stage === 'activationSetup' && 'Account Activation'}
                                        {stage === 'resetPassword' && 'Password Recovery'}
                                        {stage === 'forgotPasswordSchoolSelection' && 'School Selection'}
                                        {stage === 'resetPasswordOtp' && 'Reset Password'}
                                    </span>
                                </div>
                            </div>

                            {/* Error and Success Messages */}
                            {error && (
                                <div
                                    className="mb-6 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-start gap-2">
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {successMessage && (
                                <div
                                    className="mb-6 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm flex items-start gap-2">
                                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{successMessage}</span>
                                </div>
                            )}

                            {/* LOGIN FORM */}
                            {stage === 'login' && (
                                <div className="space-y-5">
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="email"
                                            className="block text-sm font-medium text-gray-700">Email</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="password"
                                            className="block text-sm font-medium text-gray-700">Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-sm">
                                        <label className="inline-flex items-center gap-2 select-none cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={() => setRememberMe(!rememberMe)}
                                                className="h-4 w-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                                            />
                                            <span className="text-gray-700">Remember me</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="text-orange-600 hover:text-orange-700 font-medium"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleLogin}
                                        disabled={isLoading}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <div
                                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <LogIn className="w-5 h-5" />
                                        )}
                                        {isLoading ? 'Signing in...' : 'Sign In'}
                                    </button>

                                    <div className="mt-6 text-center">
                                        <p className="text-sm text-gray-600">
                                            Don't have access?{' '}
                                            <button type="button"
                                                className="font-medium text-orange-600 hover:text-orange-500">
                                                Contact Administrator
                                            </button>
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* SCHOOL SELECTION */}
                            {stage === 'schoolSelection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        Your child is enrolled in multiple schools. Please select the school you want to
                                        access.
                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {availableSchools.map(school => (
                                            <div
                                                key={school.id}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-orange-500 hover:bg-orange-50 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-200'
                                                    }`}
                                                onClick={() => !isLoading && completeLogin(school.id)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <Building2 size={20} className="text-orange-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-gray-800">{school.name}</h3>
                                                        {school.domain &&
                                                            <p className="text-sm text-gray-500">{school.domain}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleBackToCredentials}
                                        disabled={isLoading}
                                        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Login
                                    </button>
                                </div>
                            )}

                            {/* ACTIVATION SETUP (temp password + new password, no OTP) */}
                            {stage === 'activationSetup' && (
                                <div className="space-y-5">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700 flex items-start gap-2">
                                        <Shield className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-medium mb-1">First-time login</p>
                                            <p>Enter your temporary password and choose a new password to activate your account.</p>
                                        </div>
                                    </div>

                                    {!prefilledEmail && (
                                        <div className="space-y-2 focus-lift transition-transform duration-200">
                                            <label htmlFor="activation-email"
                                                className="block text-sm font-medium text-gray-700">Email</label>
                                            <div className="relative">
                                                <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                    <Mail className="w-5 h-5 text-gray-400" />
                                                </span>
                                                <input
                                                    id="activation-email"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                    placeholder="Enter your email"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="temp-password"
                                            className="block text-sm font-medium text-gray-700">Temporary
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="temp-password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter temporary password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500">Provided by your administrator</p>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="new-password"
                                            className="block text-sm font-medium text-gray-700">New Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="new-password"
                                                type={showNewPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {showNewPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="confirm-password"
                                            className="block text-sm font-medium text-gray-700">Confirm
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="confirm-password"
                                                type={showNewPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                onKeyPress={(e) => handleKeyPress(e, handleActivation)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm password"
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
                                        <ul className="space-y-1">
                                            <li className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.length && <CheckCircle size={12} className="mr-1" />}
                                                At least 8 characters
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.uppercase &&
                                                    <CheckCircle size={12} className="mr-1" />}
                                                Uppercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.lowercase &&
                                                    <CheckCircle size={12} className="mr-1" />}
                                                Lowercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.number ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.number && <CheckCircle size={12} className="mr-1" />}
                                                Number
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.special ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.special && <CheckCircle size={12} className="mr-1" />}
                                                Special character
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.match ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.match && <CheckCircle size={12} className="mr-1" />}
                                                Passwords match
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={goToLogin}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleActivation}
                                            disabled={isLoading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {isLoading ? 'Activating...' : 'Activate Account'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* FORGOT PASSWORD */}
                            {stage === 'resetPassword' && (
                                <div className="space-y-5">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700">
                                        Enter your email to find your account and reset your password.
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-email"
                                            className="block text-sm font-medium text-gray-700">Email</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onKeyPress={(e) => handleKeyPress(e, handleForgotPasswordRequest)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={goToLogin}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleForgotPasswordRequest}
                                            disabled={isLoading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {isLoading ? 'Finding Account...' : 'Continue'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* FORGOT PASSWORD SCHOOL SELECTION */}
                            {stage === 'forgotPasswordSchoolSelection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        We found your account in multiple schools. Please select the school where you want to reset your password.
                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {forgotPasswordSchools.map(school => (
                                            <div
                                                key={school.id}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-orange-500 hover:bg-orange-50 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-200'
                                                    }`}
                                                onClick={() => !isLoading && sendPasswordResetOTP(school.id)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <Building2 size={20} className="text-orange-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-gray-800">{school.name}</h3>
                                                        <p className="text-sm text-gray-500">Click to reset password for this school</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setStage('resetPassword')}
                                        disabled={isLoading}
                                        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Email Entry
                                    </button>
                                </div>
                            )}

                            {/* RESET PASSWORD OTP */}
                            {stage === 'resetPasswordOtp' && (
                                <div className="space-y-5">
                                    <div className="text-center mb-2">
                                        <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                                            <Clock className="h-4 w-4" />
                                            <span
                                                className="text-sm font-medium">Expires in {formatTime(otpTimeLeft)}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">6-digit
                                            verification code</label>
                                        <div className="flex justify-center space-x-2" onPaste={handleOtpPaste}>
                                            {otp.map((digit, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => (otpRefs.current[idx] = el)}
                                                    type="text"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                                                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                                    className="w-10 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-new-password"
                                            className="block text-sm font-medium text-gray-700">New Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-new-password"
                                                type={showNewPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {showNewPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> :
                                                    <Eye className="w-5 h-5 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-confirm-password"
                                            className="block text-sm font-medium text-gray-700">Confirm
                                            Password</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="w-5 h-5 text-gray-400" />
                                            </span>
                                            <input
                                                id="reset-confirm-password"
                                                type={showNewPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm password"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setStage('resetPassword')}
                                            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handlePasswordReset}
                                            disabled={isLoading}
                                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-70"
                                        >
                                            {isLoading ? 'Resetting...' : 'Reset Password'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Compliance Marquee */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="marquee-outer text-center text-xs md:text-sm font-medium py-1">
                                    <div className="marquee-track">
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National
                                                Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment
                                                    Framework</strong>.
                                        </div>
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National
                                                Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment
                                                    Framework</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Login;